<script setup>
import PlusIcon from '@iconify-vue/ph/plus';
import PlusCircleIcon from '@iconify-vue/ph/plus-circle-fill';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type = undefined, archived = undefined } = defineProps({
    type: {
        required: false,
        type: String,
    },
    archived: {
        required: false,
        type: Boolean,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

function getContactsWith(contacts, key) {
    return contacts.filter((contact) => contact[key]);
}

function getContactTooltipContent(contacts, key, withName) {
    if (contacts.length < 2) {
        return null;
    }

    return h(
        'ul',
        {
            class: withName
                ? 'list-disc list-inside !m-0 px-2 py-1'
                : 'list-disc list-inside !m-0',
        },
        contacts.slice(1).map((contact) =>
            h('li', {
                innerHTML:
                    withName && contact.name
                        ? "<b class='uppercase'>" +
                          contact.name +
                          ' :</b> ' +
                          contact[key]
                        : contact[key],
            }),
        ),
    );
}

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    selectionIds,
    exportData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route(
                        'front.projectManagement.customer.updateCustomer',
                        {
                            tenant: props.row.original.id,
                        },
                    ),
                    target: '_self',
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'contactName1',
            header: () => 'Nom',
            cell: (props) => {
                const contacts = getContactsWith(
                    props.row.original.contacts,
                    'name',
                );

                return contacts.length > 0
                    ? h(TextCell, {
                          class: 'uppercase',
                          text: contacts[0].name,
                          tooltipIcon: PlusCircleIcon,
                          tooltipPlacement: 'bottom',
                          tooltipContent: getContactTooltipContent(
                              contacts,
                              'name',
                              false,
                          ),
                      })
                    : null;
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'contactEmail1',
            header: () => 'Email',
            cell: (props) => {
                const contacts = getContactsWith(
                    props.row.original.contacts,
                    'email',
                );

                return contacts.length > 0
                    ? h(TextCell, {
                          text: contacts[0].email,
                          tooltipIcon: PlusCircleIcon,
                          tooltipPlacement: 'bottom',
                          tooltipContent: getContactTooltipContent(
                              contacts,
                              'email',
                              true,
                          ),
                      })
                    : null;
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'contactPhone1',
            header: () => 'Téléphone',
            cell: (props) => {
                const contacts = getContactsWith(
                    props.row.original.contacts,
                    'phone',
                );

                return contacts.length > 0
                    ? h(TextCell, {
                          text: contacts[0].phone,
                          tooltipIcon: PlusCircleIcon,
                          tooltipPlacement: 'bottom',
                          tooltipContent: getContactTooltipContent(
                              contacts,
                              'phone',
                              true,
                          ),
                      })
                    : null;
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'serviceStartDate',
            header: () => 'Début prestation',
            cell: (props) =>
                props.row.original.serviceStartDate
                    ? h(TextCell, {
                          text: props.row.original.serviceStartDate,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'createdAt',
            header: () => 'Créé le',
            cell: (props) =>
                props.row.original.createdAt
                    ? h(TextCell, {
                          text: props.row.original.createdAt,
                      })
                    : null,
        }),
    ],
    url: route('front.projectManagement.customer.postContacts'),
    exportUrl: route('front.projectManagement.customer.postExportContacts'),
    data: {
        type,
        archived,
    },
    selectionIdKey: 'id',
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />

            <ActionButton
                size="small"
                :href="route('front.projectManagement.customer.createCustomer')"
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
